/*
 * TFR Shoes – "Comprar pelo WhatsApp".
 *
 * Lê o estado real do seletor de variantes do Dawn (variant-selects) e mantém o link do botão
 * atualizado. Não usa formulário de carrinho, não faz requisições de compra e nunca envia
 * mensagens: só abre o WhatsApp com o texto preenchido para a cliente decidir enviar.
 *
 * Regras:
 *  - Opções com mais de um valor precisam de escolha explícita (clique da cliente) ou de uma
 *    variante já indicada na URL (?variant=ID). A variante pré-selecionada pelo tema não conta.
 *  - Combinação inexistente: pede outra escolha.
 *  - Variante indisponível: "Consultar disponibilidade", sem prometer estoque ou reposição.
 */
(function () {
  'use strict';

  const OPTIONS_PLACEHOLDER = '{opcoes}';

  function waNumber(raw) {
    return String(raw || '').replace(/\D/g, '');
  }

  function productUrlWithVariant(baseUrl, variantId) {
    try {
      const url = new URL(baseUrl, window.location.origin);
      if (variantId) url.searchParams.set('variant', String(variantId));
      return url.toString();
    } catch (e) {
      if (!variantId) return baseUrl;
      return baseUrl + (baseUrl.indexOf('?') === -1 ? '?' : '&') + 'variant=' + encodeURIComponent(variantId);
    }
  }

  /** Monta a mensagem a partir do modelo configurado no editor do tema. */
  function buildMessage(template, data) {
    const optionLines = (data.selections || [])
      .filter((s) => s && s.value)
      .map((s) => s.name + ': ' + s.value)
      .join('\n');

    const lines = String(template || '').replace(/\r\n?/g, '\n').split('\n');
    const out = [];
    lines.forEach((line) => {
      if (line.trim() === OPTIONS_PLACEHOLDER) {
        if (optionLines) out.push(optionLines);
        return;
      }
      out.push(
        line
          .split('{produto}').join(data.title || '')
          .split('{link}').join(data.link || '')
          .split(OPTIONS_PLACEHOLDER).join(optionLines.replace(/\n/g, ' · '))
      );
    });
    return out.join('\n').replace(/\n{3,}/g, '\n\n').trim();
  }

  function buildWaUrl(number, message) {
    const n = waNumber(number);
    return 'https://wa.me/' + n + (message ? '?text=' + encodeURIComponent(message) : '');
  }

  function findVariant(variants, values) {
    return (
      (variants || []).find((v) => v.options.length === values.length && v.options.every((o, i) => o === values[i])) ||
      null
    );
  }

  function joinNames(names) {
    const quoted = names.map((n) => '“' + n + '”');
    if (quoted.length <= 1) return quoted.join('');
    return quoted.slice(0, -1).join(', ') + ' e ' + quoted[quoted.length - 1];
  }

  window.TfrWhatsApp = { buildMessage, buildWaUrl, findVariant, productUrlWithVariant, waNumber };

  if (typeof customElements === 'undefined' || customElements.get('tfr-whatsapp-cta')) return;

  class TfrWhatsappCta extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      try {
        this.data = JSON.parse(this.querySelector('[data-tfr-wa-data]').textContent);
      } catch (e) {
        console.error('[TFR] Dados do WhatsApp inválidos', e);
        return;
      }

      const DEFAULT_HINTS = {
        choose: 'Escolha {opcoes} para continuar.',
        invalid: 'Essa combinação não está disponível. Escolha outra opção.',
        unavailable: 'Esta opção está indisponível no momento. Fale com a gente para consultar.',
      };
      const DEFAULT_TEMPLATES = {
        buy: 'Olá! Quero comprar este modelo da TFR Shoes:\nProduto: {produto}\n{opcoes}\nLink: {link}',
        unavailable: 'Olá! Gostaria de consultar a disponibilidade deste modelo da TFR Shoes:\nProduto: {produto}\n{opcoes}\nLink: {link}',
      };
      this.data.hints = Object.assign({}, DEFAULT_HINTS, this.data.hints);
      this.data.templates = Object.assign({}, DEFAULT_TEMPLATES, this.data.templates);
      Object.keys(DEFAULT_HINTS).forEach((k) => { if (!this.data.hints[k]) this.data.hints[k] = DEFAULT_HINTS[k]; });
      Object.keys(DEFAULT_TEMPLATES).forEach((k) => { if (!this.data.templates[k]) this.data.templates[k] = DEFAULT_TEMPLATES[k]; });

      this.button = this.querySelector('[data-tfr-wa-button]');
      this.labelEl = this.querySelector('[data-tfr-wa-label]');
      this.hintEl = this.querySelector('[data-tfr-wa-hint]');
      this.root = this.closest('product-info') || this.closest('.shopify-section') || document.body;
      this.fallbackHref = this.button.getAttribute('href');

      // Valores possíveis de cada opção (a partir das variantes cadastradas).
      const opts = this.data.options || [];
      this.valuesByOption = opts.map((_, i) => Array.from(new Set(this.data.variants.map((v) => v.options[i]))));
      this.requiredOptions = opts.filter((_, i) => this.valuesByOption[i].length > 1);
      this.chosen = new Set(this.data.preselected ? opts : []);

      this.onInteract = this.onInteract.bind(this);
      this.root.addEventListener('click', this.onInteract, true);
      this.root.addEventListener('change', this.onInteract, true);
      this.root.addEventListener('keydown', this.onKeydown.bind(this), true);
      this.button.addEventListener('click', this.onButtonClick.bind(this));

      // O Dawn re-renderiza o seletor a cada troca; reaplica o estado depois disso.
      this.observer = new MutationObserver((mutations) => {
        if (mutations.every((m) => this.contains(m.target))) return;
        this.schedule();
      });
      this.observer.observe(this.root, { childList: true, subtree: true });

      this.update();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.root?.removeEventListener('click', this.onInteract, true);
      this.root?.removeEventListener('change', this.onInteract, true);
      this._ready = false;
    }

    get variantSelects() {
      return this.root.querySelector('variant-selects');
    }

    optionNameOf(el) {
      if (!el) return null;
      if (el.tagName === 'SELECT') return el.selectedOptions[0]?.dataset.optionName || el.options[0]?.dataset.optionName;
      return el.dataset.optionName || null;
    }

    onInteract(event) {
      const selects = this.variantSelects;
      if (!selects || !selects.contains(event.target)) return;
      const control = event.target.closest('input, select, label');
      if (!control) return;
      let el = control;
      if (control.tagName === 'LABEL') el = control.control || document.getElementById(control.htmlFor);
      if (el && el.disabled) return;
      const name = this.optionNameOf(el);
      if (name) this.chosen.add(name);
      this.schedule();
    }

    onKeydown(event) {
      // Navegar por setas troca a opção e dispara "change"; Espaço/Enter numa opção já marcada
      // também conta como escolha.
      if (event.key !== ' ' && event.key !== 'Enter') return;
      this.onInteract(event);
    }

    schedule() {
      cancelAnimationFrame(this._raf);
      this._raf = requestAnimationFrame(() => this.update());
    }

    /** Valores atualmente marcados no seletor do Dawn, na ordem das opções do produto. */
    readSelectedValues() {
      const selects = this.variantSelects;
      return (this.data.options || []).map((name, i) => {
        if (this.valuesByOption[i].length === 1) return this.valuesByOption[i][0];
        if (!selects) return null;
        const radios = Array.from(selects.querySelectorAll('input[type="radio"]')).filter(
          (r) => r.dataset.optionName === name
        );
        if (radios.length) return radios.find((r) => r.checked)?.value ?? null;
        const select = Array.from(selects.querySelectorAll('select')).find((s) =>
          Array.from(s.options).some((o) => o.dataset.optionName === name)
        );
        return select ? select.value : null;
      });
    }

    pendingOptions() {
      return this.requiredOptions.filter((name) => !this.chosen.has(name));
    }

    paintPending(pending) {
      const selects = this.variantSelects;
      if (!selects) return;
      selects.querySelectorAll('fieldset, .product-form__input--dropdown').forEach((group) => {
        const name = this.optionNameOf(group.querySelector('[data-option-name], select'));
        const isPending = !!name && pending.includes(name);
        group.classList.toggle('tfr-opt--pending', isPending);
        if (!isPending) group.classList.remove('tfr-opt--attention');
      });
    }

    setHint(text) {
      if (!this.hintEl) return;
      this.hintEl.textContent = text || '';
      this.hintEl.classList.toggle('is-visible', !!text);
    }

    update() {
      if (!this.data) return;
      const pending = this.pendingOptions();
      this.paintPending(pending);

      const values = this.readSelectedValues();
      const variant = pending.length ? null : findVariant(this.data.variants, values);
      const selections = (this.data.options || []).map((name, i) => ({ name, value: values[i] }));

      if (pending.length) {
        this.state = 'pending';
        this.setLabel(this.data.labels.buy);
        this.button.setAttribute('href', this.fallbackHref);
        this.setHint(this._attention ? this.data.hints.choose.split('{opcoes}').join(joinNames(pending)) : '');
        this.button.setAttribute('aria-disabled', 'true');
        return;
      }

      this._attention = false;
      if (!variant) {
        this.state = 'invalid';
        this.setLabel(this.data.labels.buy);
        this.button.setAttribute('href', this.fallbackHref);
        this.button.setAttribute('aria-disabled', 'true');
        this.setHint(this.data.hints.invalid);
        return;
      }

      const available = !!variant.available;
      this.state = available ? 'ready' : 'unavailable';
      const link = productUrlWithVariant(this.data.url, variant.id);
      const template = available ? this.data.templates.buy : this.data.templates.unavailable;
      const message = buildMessage(template, { title: this.data.title, link, selections });
      this.button.setAttribute('href', buildWaUrl(this.data.number, message));
      this.button.removeAttribute('aria-disabled');
      this.setLabel(available ? this.data.labels.buy : this.data.labels.unavailable);
      this.button.classList.toggle('tfr-wa-cta__button--consult', !available);
      this.setHint(available ? '' : this.data.hints.unavailable);
      this.dataset.variantId = String(variant.id);
    }

    setLabel(text) {
      if (this.labelEl && this.labelEl.textContent !== text) this.labelEl.textContent = text;
    }

    onButtonClick(event) {
      this.update();
      if (this.state === 'ready' || this.state === 'unavailable') return;

      event.preventDefault();
      const pending = this.pendingOptions();
      this._attention = true;
      this.update();

      const selects = this.variantSelects;
      if (!selects) return;
      const groups = Array.from(selects.querySelectorAll('fieldset, .product-form__input--dropdown'));
      const target =
        groups.find((g) => g.classList.contains('tfr-opt--pending')) || (this.state === 'invalid' ? groups[0] : null);
      groups.forEach((g) => {
        if (g.classList.contains('tfr-opt--pending')) {
          g.classList.remove('tfr-opt--attention');
          void g.offsetWidth; // reinicia a animação
          g.classList.add('tfr-opt--attention');
        }
      });
      if (target) {
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
        const focusable = target.querySelector('input:not(.disabled):not([disabled]), input, select');
        focusable?.focus({ preventScroll: true });
      }
      if (!pending.length && this.state === 'invalid') this.setHint(this.data.hints.invalid);
    }
  }

  customElements.define('tfr-whatsapp-cta', TfrWhatsappCta);
})();
